<script setup>
import NotificationsSidebar from "@/Components/notifications/NotificationsSidebar.vue";
import { ref } from "vue";
import TeamSwitcher from "@/Components/UI/nav/TeamSwitcher.vue";
import BaseUserAvatar from "@/Components/users/BaseUserAvatar.vue";
import { Link } from "@inertiajs/vue3";
import NavMenuLeft from "@/Components/UI/nav/NavMenuLeft.vue";
import SubscriptionButton from "@/Components/UI/nav/SubscriptionButton.vue";
import ContactSalesButton from "@/Components/organization/ContactSalesButton.vue";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";

const sidebar = ref(null);
const notifications = ref(false);
const showNavigation = ref(false);
defineProps({
    currentType: {
        type: String,
        default: "",
    },
    startTutorial: {
        type: Function,
        default: null,
    },
});

const showNotificationsSidebar = () => {
    showNavigation.value = false;
    sidebar.value.show();
};
</script>

<template>
    <div
        class="fixed z-50 h-32 bg-white w-screen flex justify-between items-center text-xs border-b-2 border-light-2 py-5 px-5 md:px-10">
        <Link
            href="/"
            class="text-dark-light flex flex-col gap-2 items-start md:flex-row md:items-center md:gap-10">
            <div class="flex flex-col gap-1 items-start">
                <img
                    class="h-7 min-w-40 md:h-10"
                    src="/storage/images/logo-dobyto.svg"
                    alt="Добыто." />
                <span class="text-2xs pl-3 ml-px leading-tight md:hidden">Кадровый документооборот</span>
            </div>
            <span class="text-dark-light hidden md:block">
                Сервис по подписанию документов
                <br />
                и документооборот с кадрами
            </span>
            <span
                v-if="!$page.props.auth.user"
                class="whitespace-nowrap text-2xs md:hidden">
                Сервис электронного документооборота
            </span>
        </Link>

        <div
            v-if="$page.props.auth.user"
            id="nav-menu-top"
            data-tutorial="nav-menu-top"
            class="flex gap-5">
            <ContactSalesButton
                v-if="! $page.props.collect_feedback"
                class="hidden lg:block" />

            <BaseButton variant="primary"
                v-if="$page.props.collect_feedback"
                component="a"
                href="https://docs.google.com/forms/d/e/1FAIpQLSfUizS5SwqWOSgDLPsLWPLZXQ6IBZtxLaNzZs-A5IuNTSUMwQ/viewform?usp=dialog"
                class="hidden md:flex">
                Заявка на улучшение
            </BaseButton>

            <SubscriptionButton
                v-if="$page.props.permissions.view.payments"
                class="hidden lg:flex" />

            <TeamSwitcher class="hidden md:flex" v-tooltip.bottom="'Сменить организацию или открыть личный кабинет'" />

            <BaseButton variant="secondary"
                :class="showNavigation ? 'text-accent-blue border-accent-blue stroke-accent-blue fill-accent-blue' : ''"
                class="flex lg:hidden fill-dark-light"
                square
                @click="showNavigation = !showNavigation">
                <svg
                    v-if="showNavigation"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    stroke="none"
                    height="16">
                    <path d="M2.49902 13.1064L13.1056 2.49984L13.8127 3.20695L3.20613 13.8136L2.49902 13.1064Z" />
                    <path d="M3.20605 2.5L13.8127 13.1066L13.1055 13.8137L2.49895 3.20711L3.20605 2.5Z" />
                </svg>
                <svg
                    v-else
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 14"
                    stroke="none"
                    height="14">
                    <path d="M0.5 0.5H15.5V1.5H0.5V0.5Z" />
                    <path d="M0.5 6.5H15.5V7.5H0.5V6.5Z" />
                    <path d="M0.5 12.5H15.5V13.5H0.5V12.5Z" />
                </svg>
            </BaseButton>

            <BaseButton variant="secondary"
                v-if="startTutorial"
                class="flex stroke-dark-light"
                square
                @click="startTutorial">
                <svg
                    width="15"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 15 16"
                    height="16"
                    fill="none">
                    <path
                        d="M7.5 9.5V8H8C8.82843 8 9.5 7.32843 9.5 6.5V6.4C9.5 5.6268 8.8732 5 8.1 5H7.5C6.67157 5 6 5.67157 6 6.5M7 11H8M7.5 15C3.63401 15 0.5 11.866 0.5 8C0.5 4.13401 3.63401 1 7.5 1C11.366 1 14.5 4.13401 14.5 8C14.5 11.866 11.366 15 7.5 15Z" />
                </svg>
            </BaseButton>

            <BaseButton variant="secondary"
                square
                class="hidden lg:flex stroke-dark-light"
                v-tooltip.bottom="'Уведомления о событиях и действиях в системе'"
                @click="showNotificationsSidebar()">
                <svg
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    height="16"
                    fill="none">
                    <path
                        d="M1.5 11H14.5M3 11V6C3 3.23858 5.23858 1 8 1C10.7614 1 13 3.23858 13 6V11M6 12.5V13C6 14.1046 6.89543 15 8 15C9.10457 15 10 14.1046 10 13V12.5" />
                </svg>
                <span
                    v-if="$page.props.unread_notifications"
                    class="absolute h-4 w-4 rounded-full bg-accent-red top-4 right-4"></span>
            </BaseButton>

            <BaseUserAvatar
                :user="$page.props.auth.user"
                class="hidden md:block"
                v-tooltip.bottom="'Перейти в настройки профиля'"
                size="lg" />
            <BaseUserAvatar
                :user="$page.props.auth.user"
                class="block md:hidden"
                size="md" />
        </div>
        <div v-else>
            <Link
                :href="route('login')"
                :class="
                    route().current('login')
                        ? 'text-blue-1 stroke-blue-1 pointer-events-none'
                        : 'text-dark-light stroke-dark-light'
                "
                class="flex p-5 w-full transition duration-200 hover:text-blue-1 hover:stroke-blue-1 cursor-pointer rounded">
                <div class="flex w-full items-center gap-3 lg:gap-5">
                    <p>Войти</p>
                    <svg
                        width="15"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 15 15"
                        height="15"
                        fill="none">
                        <path
                            stroke-linecap="square"
                            stroke="currentColor"
                            fill-rule="evenodd"
                            d="M10.5 3.49804C10.5 5.15396 9.157 6.49609 7.5 6.49609C5.843 6.49609 4.5 5.15396 4.5 3.49804C4.5 1.84212 5.843 0.5 7.5 0.5C9.157 0.5 10.5 1.84212 10.5 3.49804Z"
                            clip-rule="evenodd" />
                        <path
                            stroke-linecap="square"
                            stroke="currentColor"
                            fill-rule="evenodd"
                            d="M12.5 14.4909H2.5C2.5 13.7808 2.5 13.1053 2.5 12.4936C2.5 10.8368 3.84315 9.49414 5.5 9.49414H9.5C11.1569 9.49414 12.5 10.8368 12.5 12.4936C12.5 13.1053 12.5 13.7808 12.5 14.4909Z"
                            clip-rule="evenodd" />
                    </svg>
                </div>
            </Link>
        </div>
    </div>
    <NotificationsSidebar
        ref="sidebar"
        @show="notifications = true"
        @close="notifications = false" />

    <NavMenuLeft
        v-if="$page.props.auth.user"
        :current-type="currentType"
        :class="[
            'transition-transform duration-200 flex lg:hidden',
            showNavigation ? 'translate-x-0' : '-translate-x-full',
        ]"
        @show-notifications="showNotificationsSidebar()" />
</template>
